/*
*   @site           Preloaded: Site
*   @function       Navigation Controller
*   @author         Last17
*   @copyright      Copyright 2025, Preloaded.com
*   @version        0.01
*
*********************************************************************************************/


//-----------------------------------------------
// Imports
//-----------------------------------------------

//Controllers
import * as Globals from '../_constants/Globals';
import LayoutController from './LayoutController';

//Libs
import { gsap } from 'gsap';
import ModuleController from './ModuleController';
import KeylineController from './KeylineController';


//-----------------------------------------------
// Class Definition
//-----------------------------------------------
class _NavigationController {

  //-----------------------------------------------
  // Constructor
  //-----------------------------------------------
  constructor() {
    //Main element
    this.element = document.querySelector('nav');
    this.pageWrapper = document.querySelector('.nav__content__pages');

    //Menu
    this.menu = this.element.querySelector('#menu .menu');

    //Keyline
    this.keyline = this.element.querySelector('.keyline');
    this.keylineSVG = this.keyline.querySelector('svg');
    this.keylinePath = this.keylineSVG.querySelector('path');
    
    //First module calculations
    this.validFirstModuleTypes = ['module--hero', 'module--header-image'];
    this.requiresValidType = true;
    this.firstModuleHeight = 0;
    this.moduleHeightBuffer = 80; //how much we take off the first module height before we show the transparent nav

    //Colour Options
    this.config = {
      theme: {
        bar: '',
        fill: '',
      },
      page: '',
      isOpen: false,
      hasLogo: false,
      iframeInit: false,
      alt: {
        left: '',
        right: '',
      },
      navAlt: document.querySelector('.nav-alt')
    };

    //Index Alt elements
    this.altElementsIndex();

    //Index the pages for toggling
    this.pageIndex();

    //Setup the first page (withou revealing it)
    this.pageSet('menu');

    //Set up the current theme for the main bar
    this.calculateBarTheme();

    //Listen to resize and scroll events
    PubSub.subscribe('resize', (id, data) => { this.resizeEvent(data); });
    PubSub.subscribe('scroll', (id, data) => { this.scrollEvent(data); });
  }


  //-----------------------------------------------
  // Alt elements
  //-----------------------------------------------
  altElementsIndex() {
    let items = this.element.querySelectorAll('.js-nav-alt');
    let itemID, itemType;
    this.altItems = {};
    for(let i = 0; i < items.length; i++) {
      //ID
      itemID = items[i].getAttribute('data-alt-id');
      itemType = items[i].getAttribute('data-alt-type');

      //Register
      this.altItems[itemID] = {
        element: items[i],
        id: itemID,
        type: itemType
      };

      //Set
      if(items[i].classList.contains('active')) {
        this.altSet(itemID, itemType);
      }
    }
  }


  //-----------------------------------------------
  // Alt set
  //-----------------------------------------------
  altSet(id, type) {
    if(id !== this.config.alt[type]) {

      
      //Old off
      if(this.config.alt[type] !== '') {
        this.altItems[this.config.alt[type]].element.classList.remove('active');
      }

      //New on
      if(id !== '') { 
        this.altItems[id].element.classList.add('active');
      }
      
      //Set
      this.config.alt[type] = id;
    }
  }


  //-----------------------------------------------
  // Page Index
  //-----------------------------------------------
  pageIndex() {
    //Pages
    this.pages = [];
    let pages = this.element.querySelectorAll('.menu-page');

    //Loop
    for(let i = 0; i < pages.length; i++) {
      //Hide
      gsap.to(pages[i], {opacity: 0, duration:0});
      
      //Index
      this.pages.push({
        element:pages[i],
        id: pages[i].getAttribute('id')
      });
    }
  }

  //-----------------------------------------------
  // Page Set/Show
  //-----------------------------------------------
  pageSet(id, reveal, theme) {

    //Theme and reveal default
    reveal = (reveal === undefined) ? false : reveal;
    theme = (theme === undefined) ? false : theme;
    

    //Act if not already the active page?
    if(id !== this.config.page) {
      //Loop and show/hide the pages accordingly
      for(let i = 0; i < this.pages.length; i++) {
        (this.pages[i].id === id) ? this.pages[i].element.classList.remove('hidden') : this.pages[i].element.classList.add('hidden');
      }

      //Set
      this.config.page = id;
    }

    //Theme
    if(theme) {
      this.themeSet(id === 'contact' ? 'silver' : 'random');
    }

    //Padding
    if(id === 'contact' && !this.pageWrapper.classList.contains('padded')) {
      this.pageWrapper.classList.add('padded');
    } else if(id !== 'contact' && this.pageWrapper.classList.contains('padded')) {
      this.pageWrapper.classList.remove('padded');
    }

    //Page reveal
    if(reveal) {
      //Animate
      let pageCurrent = this.pageFind(this.config.page);
      gsap.killTweensOf(pageCurrent.element);
      gsap.fromTo(pageCurrent.element, {opacity:0, y:10}, { y:0, opacity: 1, duration: 0.4, delay: 0.1, ease:'power1.inOut'});

      //IFrame: Deprecated September 2025
      /*if(id === 'contact' && !this.config.iframeInit) {
        iFrameResize({ log: true }, '#contact-form') ;
        this.config.iframeInit = true;
      }*/

      //Handle the nav
      this.altSet((id === 'menu') ? 'logo' : 'contact-left', 'left');
      this.altSet((id === 'menu') ? 'contact-right' : '', 'right');
    }
  }

  //-----------------------------------------------
  // Find
  //-----------------------------------------------
  pageFind(id) {
    for(let i = 0; i < this.pages.length; i++) {
      if(this.pages[i].id === id) {
        return this.pages[i];
      }
    }
    return null;
  }


  //-----------------------------------------------
  // Calculate the bar theme
  //-----------------------------------------------
  calculateBarTheme() {
    //Defaults to night-sky
    let hasOverride = this.element && this.element.getAttribute('data-theme');
    let theme = hasOverride ? this.element.getAttribute('data-theme') : 'night-sky';
    if(!hasOverride) {
      //Check the modules for colours where 'night-sky' doesn't work with
      let module = document.querySelector('.module:not(.grid)');
      for(let i = 0; i < Globals.NAV_CONFIG.altBar.length; i++) {
        if(module.classList.contains('module--hero') || module.classList.contains('theme-' + Globals.NAV_CONFIG.altBar[i])) {
          theme = 'white';
          break;
        }
      }
    }

    //Set the theme on the bar
    this.config.theme.bar = theme;
    this.element.classList.add('theme-nav-bar-'+ this.config.theme.bar);
  }
  


  //-----------------------------------------------
  // Init
  //-----------------------------------------------
  init() {
    //Set layout
    this.getFirstModuleHeight();
    this.setLayout(true);

    //Nav links
    document.querySelector('.menu-toggle').addEventListener('click', function(event) {
      //Toggle
      this.menuToggle(event);

      //Stop the event 
      event.preventDefault();
      event.stopPropagation();
    }.bind(this));

    //Test
    if(document.location.href.indexOf('localhost') !== -1) {
      //this.menuToggle();
    }
  }


  //-----------------------------------------------
  // Menu Toggle
  //-----------------------------------------------
  menuToggle(event, page) {
    //Page
    page = (page === undefined) ? (document.location.href.indexOf('localhost') !== -1) ? 'menu' : 'menu' : page;

    //Act
    (!this.config.isOpen) ? this.open(page) : this.close();
  }


  //-----------------------------------------------
  // Open (called on toggle and via the contact links)
  //-----------------------------------------------
  open(page) {
    //Open and reveal the given page
    this.pageSet(page, true, true);

    //Alt Set based on the current page
    this.altSet((page === 'menu') ? 'logo' : 'contact-left', 'left');
    this.altSet((page === 'menu') ? 'contact-right' : '', 'right');

    //Store scroll (not required any more)
    //this.config.restoreScrollY = window.scrollY;
    
    //Opening
    this.element.classList.add('open');
    document.body.classList.add('no-scroll');
    document.documentElement.classList.add('no-scroll');


    //Remove other modifiers
    this.element.classList.remove('offscreen');
    this.element.classList.remove('opaque');

    //Flag
    this.config.isOpen = true;
  }

  //-----------------------------------------------
  // Set theme
  //-----------------------------------------------
  themeSet(theme) {
    //Old off
    if(this.config.theme.fill !== '') {
      this.element.classList.remove('theme-nav-fill-' + this.config.theme.fill);
    }

    if(theme === undefined || theme === 'random' || theme === '') {
      //Pick a new theme from the list (don't match the current bar theme, or the one we picked last time)
      theme = Globals.NAV_CONFIG.allowed[Math.floor(Math.random() * Globals.NAV_CONFIG.allowed.length)];
      while(theme === this.config.theme.bar || theme === this.config.theme.fill) {
        theme = Globals.NAV_CONFIG.allowed[Math.floor(Math.random() * Globals.NAV_CONFIG.allowed.length)];
      }
    }

    //Set and store
    this.element.classList.add('theme-nav-fill-' + theme);
    this.config.theme.fill = theme;
  }


  //-----------------------------------------------
  // Close
  //-----------------------------------------------
  close() {
    //Closing
    this.element.classList.remove('open');
    document.body.classList.remove('no-scroll');
    document.documentElement.classList.remove('no-scroll');

    //Snap scroll back to where it was before we opened, not required anymore
    //window.scrollTo(0, this.config.restoreScrollY);

    //Alt set
    this.altSet('logo', 'left'); // Logo, or back to X page?
    this.altSet('contact-right', 'right'); //RHS - Always 'contact'

    //Hide the current page immediately
    let pageCurrent = this.pageFind(this.config.page);
    gsap.killTweensOf(pageCurrent.element);
    gsap.to(pageCurrent.element, {opacity:0, duration: 0});

    //Flag
    this.config.isOpen = false;
    
    //Layout
    this.setLayout(true);

  }


  //-----------------------------------------------
  // Resize Event
  //-----------------------------------------------
  setLayout(checkDirection) {
    //Show / hide
    if (checkDirection && !this.config.isOpen) {
      //Not open, so check the content
      if (LayoutController.scroll.direction.y > 0 && !this.element.classList.contains('offscreen')) {
        this.displayNavBar(false);
      } else if (LayoutController.scroll.direction.y < 0 && this.element.classList.contains('offscreen')) {
        //Show it
        this.displayNavBar(true);
      }
    }

    //Handle alt nav
    if(this.config.navAlt && KeylineController.keylinesGeneric.length) {
      if(window.scrollY >=  KeylineController.keylinesGeneric[0].height && KeylineController.keylinesGeneric[0].type === 'top') {
        //Nav should be ON
        if(!this.config.navAlt.classList.contains('active')) {
          this.config.navAlt.classList.add('active');
        }
      } else {
        //Nav should be OFF
        if(this.config.navAlt.classList.contains('active')) {
          this.config.navAlt.classList.remove('active');
        }
      }
    }


    //Mess around with the transparency if the menu is not 'offscreen' (i.e. in view) AND is not open
    if(!this.element.classList.contains('offscreen') && !this.config.isOpen) {
      if (LayoutController.scroll.current.y >= this.firstModuleHeight && !this.element.classList.contains('opaque')) {
        this.element.classList.add('opaque');
      } else if (LayoutController.scroll.current.y < this.firstModuleHeight && this.element.classList.contains('opaque')) {
        this.element.classList.remove('opaque');
      }
    }
  }


  //-----------------------------------------------
  // Display/Hide nav bar
  //-----------------------------------------------
  displayNavBar(show) {
    if(!show) {
      this.element.classList.add('offscreen');
    } else {
      this.element.classList.remove('offscreen');
    }
  }




  //-----------------------------------------------
  // First module height
  //-----------------------------------------------
  getFirstModuleHeight() {
    //Minimum height in pixels to ensure we always get some area with a clean nav
    this.firstModuleHeight = LayoutController.layout === 'mobile' ? 100 : 100;
    
    //Look for valid
    if(document.querySelectorAll('.module:not(.grid)').length ) {
      //Get a list of modules
      let list = document.querySelectorAll('.module:not(.grid)');
      let targetElement = list[0]; //start at the first element

      //Only allow certain module types to be transparent
      for(let i = 0; i < this.validFirstModuleTypes.length; i++) {
        if(targetElement.classList.contains(this.validFirstModuleTypes[i]) || !this.requiresValidType) {
          //If there's a flourish next to it, then we should include that height too (on live, they are wrapped in <section> tags, so this is a little more complicated)
          while(this.nextItemFindFlourish(targetElement)) {
            targetElement = this.nextItemFindFlourish(targetElement);
          }

          //Store the height
          this.firstModuleHeight = Math.max(this.firstModuleHeight, targetElement.offsetHeight + targetElement.offsetTop - this.moduleHeightBuffer);
          break;
        }
      }

    }
  }

  //-----------------------------------------------
  // Does the next child contain a flourish
  //-----------------------------------------------
  nextItemFindFlourish(element) {
    if(element.parentNode.nextElementSibling && element.parentNode.nextElementSibling.nodeName === 'SECTION') {
      for (const child of element.parentNode.nextElementSibling.children) {
        if(child.classList.contains('flourish')) {
          return child;
        }
      }
    }

    return false;
  }


  //-----------------------------------------------
  // Resize Event
  //-----------------------------------------------
  resizeEvent(data) {
    //Re-read the height of the top module if the window height changed
    this.getFirstModuleHeight();
    
    //Set the layout again
    this.setLayout(true);
  }


  //-----------------------------------------------
  // Scroll event
  //-----------------------------------------------
  scrollEvent(data) {
    this.setLayout(data.directionChanged);
  }
}


//--------------------------------------------------------------------------
//  Export
//--------------------------------------------------------------------------
const NavigationController = new _NavigationController();
export default NavigationController;